import { GameState, BusinessNode, Stall, SaleTransaction, DailyStats, formatZoneTag } from '../domain/types.ts';
import { Command } from './commands.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';
import { SIDEWALK_MENU, getRegionalMenu, getRegionFromCoordinates, RegionKey } from '../config/menuConfig.ts';
import { DeterministicRNG } from '../simulation/rng.ts';
import { createInitialGameState } from '../simulation/seedData.ts';
import { SaveStore } from '../persistence/saveStore.ts';
import { GAME_EVENTS } from '../simulation/eventEngine.ts';
import { WeatherSystem } from '../simulation/weatherSystem.ts';
import { MilestoneSystem } from '../simulation/milestoneSystem.ts';
import { checkNextLevel, getPerkForDay } from '../config/progressionConfig.ts';
import { RivalSystem } from '../simulation/rivalSystem.ts';

export class AppStore {
  private state: GameState;
  private listeners: Array<(state: Readonly<GameState>) => void> = [];
  private rng: DeterministicRNG;
  private lastAutoSaveTime = Date.now();

  constructor() {
    const saved = SaveStore.load();
    const initial = createInitialGameState();

    if (saved && saved.dummyRivals && typeof saved.dummyRivals === 'object') {
      initial.dummyRivals = saved.dummyRivals;
    }

    if (saved && saved.player) {
      initial.player = {
        ...initial.player,
        ...saved.player,
        username: saved.player.username || '',
        stallName: saved.player.stallName || '',
        totalRevenue: saved.player.totalRevenue ?? saved.totalRevenue ?? 0,
        totalProfit: saved.player.totalProfit ?? saved.totalProfit ?? 0,
        totalSales: saved.player.totalSales ?? saved.totalSales ?? 0,
        stallLevel: saved.player.stallLevel ?? 1,
        unlockedPerks: saved.player.unlockedPerks ?? [],
      };
      initial.stalls = saved.stalls || {};
      initial.seed = saved.seed ?? initial.seed;
      initial.phase = saved.phase ?? (saved.player.stallName ? 'PLAYING' : 'BOOT');

      // Khôi phục bản đồ thực tế mà người chơi đã chọn trước đó
      if (saved.neighborhood) {
        initial.neighborhood = saved.neighborhood;
      }

      // Khôi phục thời tiết đang diễn ra
      if (saved.weather) {
        initial.weather = saved.weather;
      }

      if (typeof saved.dayNumber === 'number') {
        initial.dayNumber = saved.dayNumber;
      }
      if (saved.dayStats) {
        initial.dayStats = saved.dayStats;
      } else if (typeof saved.dayNumber === 'number') {
        initial.dayStats = {
          dayNumber: saved.dayNumber,
          startedAt: Date.now(),
          revenue: 0,
          cost: 0,
          profit: 0,
          salesCount: 0,
          rentPaid: 0,
        };
      }
      if (Array.isArray(saved.dayHistory)) {
        initial.dayHistory = saved.dayHistory;
      }

      if (saved.currentShift) {
        initial.currentShift = saved.currentShift;
      }
      if (typeof saved.isWeekend === 'boolean') {
        initial.isWeekend = saved.isWeekend;
      }
      if (typeof saved.isOvertimeActive === 'boolean') {
        initial.isOvertimeActive = saved.isOvertimeActive;
      }
      if (saved.dayOfWeek) {
        initial.dayOfWeek = saved.dayOfWeek;
      } else if (typeof saved.dayNumber === 'number') {
        const DAYS_OF_WEEK = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];
        initial.dayOfWeek = DAYS_OF_WEEK[(saved.dayNumber - 1) % 7]!;
        initial.isWeekend = initial.dayOfWeek === 'Thứ Bảy' || initial.dayOfWeek === 'Chủ Nhật';
      }

      // Đồng bộ ownerId cho nodes trên bản đồ
      if (initial.neighborhood) {
        const ownedNodeIds = new Set(
          initial.player.ownedStalls.map((sid) => initial.stalls[sid]?.nodeId)
        );
        initial.neighborhood.nodes = initial.neighborhood.nodes.map((n) => ({
          ...n,
          ownerId: ownedNodeIds.has(n.id) ? initial.player.id : undefined,
        }));

        // Sanitize activeMenu chuẩn xác theo vùng miền
        const regMenu = getRegionalMenu(
          initial.neighborhood.center.lat,
          initial.neighborhood.center.lon,
          initial.neighborhood.label
        );
        const validActive = (initial.player.activeMenu ?? []).filter((id) =>
          regMenu.some((m) => m.id === id)
        );
        if (validActive.length === 0) {
          initial.player.activeMenu = regMenu.filter((m) => m.unlocked).map((m) => m.id).slice(0, 2);
        } else {
          initial.player.activeMenu = validActive;
        }
      }

      if (saved.narrativeQuote) {
        initial.narrativeQuote = saved.narrativeQuote;
      } else if (saved.neighborhood) {
        const areaName = saved.neighborhood.label.split(',')[0]?.trim() || 'vỉa hè';
        if (saved.player?.stallName && saved.player.ownedStalls && saved.player.ownedStalls.length > 0) {
          initial.narrativeQuote = `👵 Bà Trà Đá: "Quán ${saved.player.stallName} ở ${areaName} hôm nay buôn bán rôm rả ghê, chúc sếp đắt hàng!"`;
        } else {
          initial.narrativeQuote = `👵 Bà Trà Đá: "Chào mừng sếp đến với ${areaName}! Tìm góc vỉa hè đẹp rồi dọn hàng ra nào."`;
        }
      }

      if (saved.currentTrendingTopic) {
        initial.currentTrendingTopic = saved.currentTrendingTopic;
      }
      if (saved.trendingItems) {
        initial.trendingItems = saved.trendingItems;
      }
    }

    this.state = initial;
    this.rng = new DeterministicRNG(this.state.seed);

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        SaveStore.save(this.state);
      });
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          SaveStore.save(this.state);
        }
      });
    }
  }

  public getState(): Readonly<GameState> {
    return this.state;
  }

  public subscribe(listener: (state: Readonly<GameState>) => void): () => void {
    this.listeners.push(listener);
    listener(this.state);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  public dispatch(command: Command): void {
    const prevState = this.state;
    this.state = this.reduce(prevState, command);
    if (this.state !== prevState) {
      for (const listener of this.listeners) {
        listener(this.state);
      }
    }
  }

  private reduce(state: GameState, command: Command): GameState {
    switch (command.type) {
      case 'SET_NEIGHBORHOOD': {
        const regionalMenu = getRegionalMenu(
          command.neighborhood.center.lat,
          command.neighborhood.center.lon,
          command.neighborhood.label
        );
        const unlockedIds = regionalMenu.filter((m) => m.unlocked).map((m) => m.id);
        const validActive = (state.player.activeMenu ?? []).filter((id) =>
          regionalMenu.some((m) => m.id === id)
        );
        const activeMenu = validActive.length > 0 ? validActive : unlockedIds.slice(0, 2);

        const playerStallId = state.player.ownedStalls[0];
        const playerNodeId = playerStallId ? state.stalls[playerStallId]?.nodeId : undefined;
        const dummyRivals =
          state.dummyRivals && Object.keys(state.dummyRivals).length > 0
            ? state.dummyRivals
            : RivalSystem.generateInitialRivals(command.neighborhood.nodes, playerNodeId, this.rng);

        const areaName = command.neighborhood.label.split(',')[0]?.trim() || 'địa bàn mới';
        const nextState = {
          ...state,
          neighborhood: command.neighborhood,
          dummyRivals,
          player: {
            ...state.player,
            activeMenu,
          },
          inspectNodeId: null,
          inspectRivalId: null,
          narrativeQuote: `👵 Bà Trà Đá: "Đã tới ${areaName}! Khu này người qua lại đông đúc, mở quán là chuẩn bài!"`,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'SET_GHOSTS':
        return {
          ...state,
          ghostStalls: command.ghosts,
        };

      case 'SET_ACTIVE_EVENT':
        return {
          ...state,
          activeEvent: command.activeEvent,
        };

      case 'SET_NARRATIVE':
        return {
          ...state,
          narrativeQuote: command.quote,
        };

      case 'SET_TRENDING_TOPIC': {
        const nextQuote = `👵 Bà Trà Đá: "Mấy đứa nghe tin gì chưa? Vụ '${command.topic}' đang rần rần trên mạng, khách khứa kéo vào bàn tán xôn xao!"`;
        const nextState = {
          ...state,
          currentTrendingTopic: command.topic,
          narrativeQuote: nextQuote,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'SET_TRENDING_ITEMS': {
        const nextState = {
          ...state,
          trendingItems: command.items,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'INSPECT_NODE':
        return {
          ...state,
          inspectNodeId: command.nodeId,
          inspectRivalId: null,
        };

      case 'INSPECT_RIVAL':
        return {
          ...state,
          inspectRivalId: command.rivalId,
          inspectNodeId: null,
        };

      case 'HANDLE_EVENT': {
        const eventTemplate = GAME_EVENTS.find((e) => e.type === state.activeEvent?.type);
        const opt = eventTemplate?.options.find((o) => o.id === command.optionId);

        let deltaMoney = opt?.moneyDelta ?? 0;
        let deltaHeat = opt?.heatDelta ?? 0;
        let deltaRep = opt?.reputationDelta ?? 0;

        const nextHeat = Math.max(0, Math.min(1.0, state.player.heat + deltaHeat));
        const nextMoney = Math.max(0, state.player.money + deltaMoney);
        const nextRep = Math.max(0, Math.min(100, state.player.reputation + deltaRep));

        const nextUpgrades = { ...state.player.upgrades };
        if (command.optionId === 'opt-open-umbrella') {
          nextUpgrades['umbrella'] = 1;
        }

        const updatedStalls = { ...state.stalls };
        const primaryStallId = state.player.ownedStalls[0];
        const primaryStall = primaryStallId ? updatedStalls[primaryStallId] : undefined;

        if (primaryStall && primaryStallId) {
          if (command.optionId === 'opt-pack-up' || command.optionId === 'opt-brawl-shelter' || command.optionId === 'opt-weather-rest') {
            updatedStalls[primaryStallId] = {
              ...primaryStall,
              isSuspended: true,
              suspendedUntil: Date.now() + 25_000,
            };
          } else if (command.optionId === 'opt-bribe-chair' || command.optionId === 'opt-buy-new-chairs') {
            updatedStalls[primaryStallId] = {
              ...primaryStall,
              isSuspended: false,
              confiscatedChairs: 0,
              suspendedUntil: undefined,
            };
          } else if (command.optionId === 'opt-brawl-repair') {
            updatedStalls[primaryStallId] = {
              ...primaryStall,
              isSuspended: false,
              suspendedUntil: undefined,
            };
          }
        }

        let quote = `Đã xử lý biến cố: ${opt?.label ?? 'Xong'}!`;
        if (command.optionId === 'opt-open-umbrella') {
          quote = '🌧️ Đã dựng ô bạt kiên cố cho quán! Từ nay mưa gió khách vẫn ngồi ăn uống bình thường.';
        } else if (command.optionId === 'opt-bribe-chair') {
          quote = '🚨 Đã nộp phạt chuộc lại bàn ghế! Quán trở lại hoạt động bình thường.';
        } else if (command.optionId === 'opt-buy-new-chairs') {
          quote = '🪑 Đã sắm bàn ghế mới thay thế! Quán đã sẵn sàng đón khách.';
        } else if (command.optionId === 'opt-pack-up') {
          quote = '📦 Đã nhanh chóng dọn bàn ghế né kiểm tra trật tự! Quán tạm nghỉ chốc lát.';
        } else if (command.optionId === 'opt-chase-bill') {
          quote = '🏃 Đã đuổi theo tóm được khách ăn quỵt đòi lại tiền thành công!';
        } else if (command.optionId === 'opt-let-go') {
          quote = '💸 Của đi thay người! Danh tiếng quán tăng lên nhờ sự rộng lượng của chủ quán.';
        } else if (command.optionId === 'opt-qr-pay') {
          quote = '📲 Đã dán biển QR thanh toán trước! Khách trả tiền ngay khi gọi món, an tâm bán hàng.';
        } else if (command.optionId === 'opt-sabotage-tea') {
          quote = '🍵 Mời chén trà dĩ hòa vi quý, đối thủ xấu hổ rút lui. Danh tiếng quán vang xa!';
        } else if (command.optionId === 'opt-brawl-repair') {
          quote = '🔨 Đã dọn dẹp sạch sẽ và sửa chữa xong bàn ghế hư hại do ẩu đả!';
        } else if (command.optionId === 'opt-tiktok-treat') {
          quote = '🌟 Idol TikTok lên video khen ngợi hết lời! Quán bùng nổ lượng khách ghé thăm!';
        }

        const nextState: GameState = {
          ...state,
          activeEvent: null,
          player: {
            ...state.player,
            money: nextMoney,
            heat: nextHeat,
            reputation: nextRep,
            upgrades: nextUpgrades,
          },
          stalls: updatedStalls,
          narrativeQuote: quote,
        };

        SaveStore.save(nextState);
        return nextState;
      }

      case 'TOGGLE_OVERTIME': {
        const nextOvertime = !state.isOvertimeActive;
        const quote = nextOvertime
          ? '🌙 Đã kích hoạt Bán Thêm Ca Đêm! Doanh thu & giá bán +30%, nhưng hãy cẩn thận trật tự đô thị.'
          : '☀️ Đã tắt Bán Thêm Ca Đêm. Quán sẽ đóng cửa khi hết ca tối.';
        const nextState: GameState = {
          ...state,
          isOvertimeActive: nextOvertime,
          narrativeQuote: quote,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'SET_SHIFT': {
        const shiftOffsets: Record<import('../domain/types.ts').DayShift, number> = {
          morning: 0,
          noon: 60_000,
          afternoon: 120_000,
          evening: 180_000,
          night: 240_000,
        };
        const offset = shiftOffsets[command.shift] ?? 0;
        const nextState: GameState = {
          ...state,
          currentShift: command.shift,
          dayStats: {
            ...state.dayStats,
            startedAt: Date.now() - offset,
          },
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'BUY_NODE': {
        if (!state.neighborhood) return state;

        const node = state.neighborhood.nodes.find((n: BusinessNode) => n.id === command.nodeId);
        if (!node) return state;

        // Nếu đã mở chính tại node này
        if (state.player.ownedStalls.some((sid) => state.stalls[sid]?.nodeId === node.id)) {
          return state;
        }

        const rival = state.dummyRivals ? state.dummyRivals[node.id] : undefined;
        let transferFee = 0;
        if (rival) {
          transferFee = rival.transferFee;
        }

        const hasOldStall = state.player.ownedStalls.length > 0;
        const relocationFee = hasOldStall ? 250_000 : 0;
        const deposit = node.unlockCost;

        // Tính toán thanh lý quán cũ nếu đã có quán trước đó (quy tắc chỉ mở 1 quán)
        let refundMoney = 0;
        let oldNodeId: string | null = null;
        let oldStallName = '';

        if (hasOldStall) {
          const oldStallId = state.player.ownedStalls[0]!;
          const oldStall = state.stalls[oldStallId];
          if (oldStall) {
            oldNodeId = oldStall.nodeId;
            const oldNode = state.neighborhood.nodes.find((n) => n.id === oldNodeId);
            // Giá trị thanh lý: 50% tiền cọc node cũ + 50% tiền ghế
            const nodeRefund = oldNode ? oldNode.unlockCost * 0.5 : 50_000;
            const chairRefund = ((oldStall.chairs - 4) / 2) * (GAME_CONFIG.economy.stoolCost * 0.5);
            refundMoney = Math.round(nodeRefund + Math.max(0, chairRefund));
            oldStallName = oldNode?.tags[0] ?? 'vỉa hè cũ';
          }
        }

        const grossCost = transferFee + relocationFee + deposit;
        const totalAvailable = state.player.money + refundMoney;

        if (totalAvailable < grossCost) {
          console.warn('[AppStore] Not enough money to buy/relocate stall:', node.id, {
            totalAvailable,
            grossCost,
            transferFee,
            relocationFee,
            deposit,
          });
          return state;
        }

        const stallId = `stall-${Date.now()}`;
        const newStall: Stall = {
          id: stallId,
          ownerId: state.player.id,
          name: state.player.stallName || 'Quán Cóc Vỉa Hè',
          nodeId: node.id,
          level: 1,
          price: GAME_CONFIG.economy.baseSpend,
          chairs: 4,
          branch: {
            capacity: 1,
            speed: 1,
            brand: 1,
          },
          reputation: Math.max(30, state.player.reputation),
          lastActiveAt: Date.now(),
        };

        // Cập nhật lại trạng thái các node trên bản đồ: node cũ về trống, node mới thuộc về player
        const updatedNodes = state.neighborhood.nodes.map((n) => {
          if (n.id === node.id) return { ...n, ownerId: state.player.id };
          if (n.id === oldNodeId) return { ...n, ownerId: undefined };
          return n;
        });

        // Xóa dummy rival tại vị trí này nếu vừa sang lại
        const updatedDummyRivals = { ...(state.dummyRivals ?? {}) };
        if (rival) {
          delete updatedDummyRivals[node.id];
        }

        const nextMoney = state.player.money + refundMoney - grossCost;

        let quote: string;
        if (rival) {
          if (rival.status === 'bankrupt') {
            quote = `👵 Bà Trà Đá: "Kèo thơm giải cứu vỡ nợ sang lại 0 ĐỒNG! Sếp chỉ tốn ${relocationFee.toLocaleString()}đ tiền xe ba gác dọn đồ là hốt trọn góc đẹp của ${rival.name}!"`;
          } else if (rival.status === 'booming' || rival.status === 'stable') {
            quote = `👵 Bà Trà Đá: "Chấp nhận chi ${transferFee.toLocaleString()}đ tiền sang nhượng (3 tháng lãi) để cướp góc đắc địa của ${rival.name}! Buôn có bạn bán có phường, góc này đắt khách lắm sếp ơi!"`;
          } else {
            quote = `👵 Bà Trà Đá: "Sang lại quán của ${rival.name} giá thanh lý ${transferFee.toLocaleString()}đ. Vào tay sếp là buôn bán phát đạt ngay!"`;
          }
        } else if (oldNodeId) {
          quote = `👵 Bà Trà Đá: "Đã dọn đồ từ ${oldStallName} (+${refundMoney.toLocaleString()}đ thanh lý) và chuyển sang địa bàn mới!"`;
        } else {
          quote = `👵 Bà Trà Đá: "Chúc mừng! Sếp đã mở thành công quán cóc đầu tiên tại ${formatZoneTag(node.tags[0])}."`;
        }

        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            money: nextMoney,
            ownedStalls: [stallId], // DUY NHẤT 1 QUÁN
            heat: Math.max(0.05, state.player.heat * 0.5), // Chuyển chỗ giảm bớt Heat cũ
          },
          neighborhood: {
            ...state.neighborhood,
            nodes: updatedNodes,
          },
          stalls: {
            [stallId]: newStall, // Lưu duy nhất quán mới
          },
          dummyRivals: updatedDummyRivals,
          inspectNodeId: node.id,
          narrativeQuote: quote,
        };

        SaveStore.save(nextState);
        return nextState;
      }

      case 'UPGRADE_STALL': {
        const stall = state.stalls[command.stallId];
        if (!stall) return state;

        const cost = GAME_CONFIG.economy.stoolCost;
        if (state.player.money < cost) return state;

        const updatedStall: Stall = {
          ...stall,
          chairs: stall.chairs + 2,
          level: stall.level + 1,
        };

        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            money: state.player.money - cost,
          },
          stalls: {
            ...state.stalls,
            [stall.id]: updatedStall,
          },
          narrativeQuote: `Nâng cấp thành công! Quán hiện có ${updatedStall.chairs} ghế.`,
        };

        SaveStore.save(nextState);
        return nextState;
      }

      case 'TICK': {
        if (state.phase === 'PAUSED' || state.isPaused) {
          return state;
        }

        let addedMoney = 0;
        let addedRevenue = 0;
        let addedCost = 0;
        let addedProfit = 0;
        let addedSalesCount = 0;
        const now = Date.now();

        // 1. Chu kỳ thời gian trong ngày (mỗi ngày 300s -> mỗi ca ~60s)
        const shiftDurationMs = 60_000;
        const startedAt = state.dayStats.startedAt || now;
        const elapsedMs = now - startedAt;

        // Tự động kết thúc ngày nếu đã hết thời gian ca (240s không bán thêm, hoặc 300s có bán ca đêm)
        const dayLimitMs = state.isOvertimeActive ? shiftDurationMs * 5 : shiftDurationMs * 4;
        if (state.phase === 'PLAYING' && !state.showDayReport && elapsedMs >= dayLimitMs && state.player.ownedStalls.length > 0) {
          return this.reduce(state, { type: 'ADVANCE_DAY' });
        }

        let nextShift: import('../domain/types.ts').DayShift = state.currentShift;
        if (elapsedMs < shiftDurationMs) {
          nextShift = 'morning';
        } else if (elapsedMs < shiftDurationMs * 2) {
          nextShift = 'noon';
        } else if (elapsedMs < shiftDurationMs * 3) {
          nextShift = 'afternoon';
        } else if (elapsedMs < shiftDurationMs * 4) {
          nextShift = 'evening';
        } else {
          if (state.isOvertimeActive) {
            nextShift = 'night';
          } else {
            nextShift = 'evening';
          }
        }

        const shiftOrder: import('../domain/types.ts').DayShift[] = ['morning', 'noon', 'afternoon', 'evening', 'night'];
        const currentIdx = shiftOrder.indexOf(state.currentShift);
        const computedIdx = shiftOrder.indexOf(nextShift);
        if (computedIdx < currentIdx) {
          nextShift = state.currentShift;
        }

        // 2. Kiểm tra Đình chỉ quán & Bàn ghế
        const primaryStallId = state.player.ownedStalls[0];
        let primaryStall = primaryStallId ? state.stalls[primaryStallId] : undefined;
        let isSuspended = false;
        let stallsUpdated = state.stalls;

        if (primaryStall && primaryStallId) {
          if (primaryStall.isSuspended && primaryStall.suspendedUntil) {
            if (now < primaryStall.suspendedUntil) {
              isSuspended = true;
            } else {
              stallsUpdated = {
                ...state.stalls,
                [primaryStallId]: {
                  ...primaryStall,
                  isSuspended: false,
                  suspendedUntil: undefined,
                },
              };
              primaryStall = stallsUpdated[primaryStallId];
            }
          }
        }

        // 3. Cơ chế Bán ca đêm Overtime (+30% doanh thu & giá bán)
        const isOvertimeNight = state.isOvertimeActive && nextShift === 'night';
        const overtimePriceBonus = isOvertimeNight ? GAME_CONFIG.overtime.priceBonus : 0;
        const overtimeMultiplier = 1 + overtimePriceBonus;

        let newSaleTransaction: SaleTransaction | null = null;

        // Xử lý các khách hàng vừa ngồi vào ghế và gọi món (nếu không bị đình chỉ)
        if (command.customers && state.player.ownedStalls.length > 0 && !isSuspended) {
          const currentMenu = state.neighborhood
            ? getRegionalMenu(state.neighborhood.center.lat, state.neighborhood.center.lon, state.neighborhood.label)
            : SIDEWALK_MENU;

          const activeMenuIds = state.player.activeMenu && state.player.activeMenu.length > 0
            ? state.player.activeMenu
            : currentMenu.filter((m) => m.unlocked).map((m) => m.id).slice(0, 2);

          let availableItems = currentMenu.filter((m) => activeMenuIds.includes(m.id));
          if (availableItems.length === 0) {
            availableItems = currentMenu.filter((m) => m.unlocked).slice(0, 2);
            state.player.activeMenu = availableItems.map((m) => m.id);
          }
          const menuToPick = availableItems.length > 0 ? availableItems : [currentMenu[0]!];
          const firstStallId = state.player.ownedStalls[0];
          const firstStall = firstStallId ? state.stalls[firstStallId] : undefined;
          const stallLevel = firstStall?.level ?? 1;
          // Năng lực phục vụ biến thiên hữu cơ theo cấp độ quán & thiết bị, không cố định
          const soloCapacity = (GAME_CONFIG.economy.soloOrderCapacity ?? 85) + (stallLevel - 1) * 8;
          const staffCapacity = (GAME_CONFIG.economy.staffOrderCapacity ?? 180) + (stallLevel - 1) * 15;
          const maxCapacity = state.player.hasStaff ? staffCapacity : soloCapacity;

          const maxPerTick = state.player.hasStaff ? 2 : 1;
          const remainingOrdersToday = Math.max(0, maxCapacity - state.dayStats.salesCount);
          let ordersServedThisTick = 0;

          for (const c of command.customers) {
            if ((c.state === 'seated' || c.state === 'takeaway') && c.hasOrdered === false) {
              if (ordersServedThisTick >= maxPerTick || ordersServedThisTick >= remainingOrdersToday) {
                // Quá tải / Đạt giới hạn phục vụ trong ngày
                continue;
              }

              c.hasOrdered = true; // Đã chốt order
              ordersServedThisTick++;

              // Khách gọi món: Áp dụng giá bán thêm giờ nếu ca đêm (+30%)
              const item = menuToPick[Math.floor(this.rng.next() * menuToPick.length)]!;
              const sellPrice = Math.round(item.sellPrice * overtimeMultiplier);
              // Perk wholesale (Day 14): Giảm 20% giá vốn toàn bộ món ăn
              const hasWholesale = state.player.unlockedPerks?.includes('perk-wholesale');
              const costPrice = hasWholesale ? Math.round(item.costPrice * 0.8) : item.costPrice;
              const itemProfit = sellPrice - costPrice;
              addedMoney += itemProfit;
              addedRevenue += sellPrice;
              addedCost += costPrice;
              addedProfit += itemProfit;
              addedSalesCount += 1;

              newSaleTransaction = {
                id: `sale-${now}-${Math.floor(this.rng.next() * 1000)}`,
                itemName: item.name,
                icon: item.icon,
                sellPrice,
                costPrice,
                profit: itemProfit,
                time: now,
                isTakeaway: c.state === 'takeaway',
              };
            }
          }
        }

        const recentSales = newSaleTransaction
          ? [newSaleTransaction, ...state.recentSales.slice(0, 19)]
          : state.recentSales;

        const latestSale = newSaleTransaction ??
          (state.latestSale && now - state.latestSale.time < 3500 ? state.latestSale : null);

        // Heat xử lý: Nếu đang bán ca đêm thì Heat tăng gấp đôi thay vì giảm!
        // Perk license (Day 30): Giảm 80% rủi ro trật tự đô thị
        const hasLicense = state.player.unlockedPerks?.includes('perk-license');
        const heatFactor = hasLicense ? 0.2 : 1.0;
        let nextHeat = state.player.heat;
        if (isOvertimeNight) {
          nextHeat = Math.min(1.0, state.player.heat + (GAME_CONFIG.heat.decayPerTick * GAME_CONFIG.overtime.heatMultiplier * heatFactor));
        } else {
          nextHeat = Math.max(0, state.player.heat - GAME_CONFIG.heat.decayPerTick);
        }
        const visualCustomers = command.customers ?? state.visualCustomers;

        const nextDayStats: DailyStats = addedSalesCount > 0
          ? {
              ...state.dayStats,
              revenue: state.dayStats.revenue + addedRevenue,
              cost: state.dayStats.cost + addedCost,
              profit: state.dayStats.profit + addedProfit,
              salesCount: state.dayStats.salesCount + addedSalesCount,
            }
          : state.dayStats;

        const totalSalesNow = (state.player.totalSales ?? 0) + addedSalesCount;
        let stallLevelNow = state.player.stallLevel ?? 1;
        let pendingCelebration = state.pendingCelebration;

        const region: RegionKey = state.neighborhood
          ? getRegionFromCoordinates(state.neighborhood.center.lat)
          : 'north';
        const nextLvlDef = checkNextLevel(totalSalesNow, stallLevelNow, region);
        if (nextLvlDef && !pendingCelebration) {
          stallLevelNow = nextLvlDef.level;
          pendingCelebration = {
            type: 'level',
            levelOrDay: nextLvlDef.level,
            title: `🎉 CHÚC MỪNG SẾP LÊN LEVEL ${nextLvlDef.level}!`,
            subtitle: nextLvlDef.title,
            items: nextLvlDef.unlockedItems.map((it) => ({
              id: it.id,
              name: it.name,
              icon: it.icon,
              desc: it.desc,
              type: 'menu' as const,
            })),
          };
        }

        const nextState: GameState = {
          ...state,
          tick: state.tick + 1,
          lastTickAt: now,
          currentShift: nextShift,
          stalls: stallsUpdated,
          player: {
            ...state.player,
            money: state.player.money + addedMoney,
            heat: nextHeat,
            totalRevenue: (state.player.totalRevenue ?? 0) + addedRevenue,
            totalProfit: (state.player.totalProfit ?? 0) + addedProfit,
            totalSales: totalSalesNow,
            stallLevel: stallLevelNow,
          },
          dayStats: nextDayStats,
          recentSales,
          latestSale,
          visualCustomers,
          pendingCelebration,
        };

        let pendingMilestone = state.pendingMilestone;
        if (!pendingMilestone) {
          const milestone = MilestoneSystem.check(nextState);
          if (milestone) {
            pendingMilestone = milestone;
          }
        }

        const finalState = {
          ...nextState,
          pendingMilestone,
        };

        // Tự động lưu tiến trình kinh doanh (doanh thu, tiền mặt, cốc bán) mà không gây nghẽn I/O
        if (addedSalesCount > 0 && now - this.lastAutoSaveTime > 3000) {
          SaveStore.save(finalState);
          this.lastAutoSaveTime = now;
        }

        return finalState;
      }

      case 'SET_PLAYER_PROFILE': {
        const username = command.username.trim() || 'Chủ Quán';
        const stallName = command.stallName.trim() || 'Quán Cóc Vỉa Hè';

        const updatedStalls = { ...state.stalls };
        if (state.player.ownedStalls.length > 0) {
          const sid = state.player.ownedStalls[0]!;
          if (updatedStalls[sid]) {
            updatedStalls[sid] = { ...updatedStalls[sid], name: stallName };
          }
        }

        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            username,
            stallName,
          },
          stalls: updatedStalls,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'START_GAME': {
        const username = state.player.username || 'Chủ Quán';
        const stallName = state.player.stallName || 'Trà Cóc Vỉa Hè';
        const areaName = state.neighborhood ? state.neighborhood.label.split(',')[0]?.trim() || 'vỉa hè' : 'vỉa hè';
        const nextState: GameState = {
          ...state,
          phase: 'PLAYING',
          player: {
            ...state.player,
            username,
            stallName,
          },
          narrativeQuote: `👵 Bà Trà Đá: "Khai trương hồng phát! Quán '${stallName}' tại ${areaName} chính thức đón khách nhé!"`,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'TOGGLE_MENU_ITEM': {
        const itemId = command.itemId;
        const currentActive = state.player.activeMenu ?? ['tra_da', 'tra_chanh'];
        let newActive: string[];
        if (currentActive.includes(itemId)) {
          if (currentActive.length <= 1) return state; // Giữ ít nhất 1 món
          newActive = currentActive.filter((id) => id !== itemId);
        } else {
          newActive = [...currentActive, itemId];
        }

        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            activeMenu: newActive,
          },
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'SET_LOADING_MESSAGE': {
        return {
          ...state,
          loadingMessage: command.message,
        };
      }

      case 'RESET_GAME': {
        SaveStore.clear();
        MilestoneSystem.reset();
        const fresh = createInitialGameState(Date.now());
        return fresh;
      }

      case 'TOGGLE_HIRE_STAFF': {
        const nextStaff = !state.player.hasStaff;
        const wage = GAME_CONFIG.economy.staffDailyWage ?? 120_000;
        const quote = nextStaff
          ? `👵 Bà Trà Đá: "Đã thuê thêm phụ quán (${wage.toLocaleString('vi-VN')}đ/ngày)! Quán làm nhanh hơn hẳn, khách đông mấy cũng phục vụ chu đáo nhé!"`
          : `👵 Bà Trà Đá: "Đã cho phụ quán nghỉ, sếp tự tay đứng quầy để tiết kiệm chi phí nhé!"`;
        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            hasStaff: nextStaff,
          },
          narrativeQuote: quote,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'ADVANCE_DAY': {
        let rentPaid = 0;
        if (state.player.ownedStalls.length > 0) {
          const stallId = state.player.ownedStalls[0];
          const stall = stallId ? state.stalls[stallId] : undefined;
          const node = stall && state.neighborhood ? state.neighborhood.nodes.find((n) => n.id === stall.nodeId) : undefined;
          rentPaid = node?.rentPerDay ?? 30_000;
        }

        const hasStall = state.player.ownedStalls.length > 0;
        const depreciationCost = hasStall ? (GAME_CONFIG.economy.dailyDepreciation ?? 25_000) : 0;
        const staffCost = (hasStall && state.player.hasStaff) ? (GAME_CONFIG.economy.staffDailyWage ?? 120_000) : 0;

        // Tính hao hụt cuối ngày cho các món nước uống pha sẵn (perishable_drink) trong activeMenu
        let totalWaste = 0;
        if (hasStall && state.dayStats.salesCount > 0) {
          const currentMenu = state.neighborhood
            ? getRegionalMenu(state.neighborhood.center.lat, state.neighborhood.center.lon, state.neighborhood.label)
            : SIDEWALK_MENU;

          const activeIds = state.player.activeMenu ?? [];
          const activeItems = currentMenu.filter((m) => activeIds.includes(m.id));

          // Kiểm tra xem có giảm hao hụt bằng thùng đá (cooler upgrade) hoặc perk-mist không
          const hasCooler = Boolean(state.player.upgrades?.['cooler']) || state.player.unlockedPerks?.includes('perk-mist');
          const wasteReductionFactor = hasCooler ? 0.3 : 1.0;

          const weatherType = state.weather?.type ?? 'sunny';
          let weatherWasteMultiplier = 1.0;
          if (weatherType === 'hot') {
            weatherWasteMultiplier = 1.5; // Nắng gắt nhanh thiu
          } else if (weatherType === 'rainy') {
            weatherWasteMultiplier = 1.3; // Mưa vắng khách ứ đọng đồ
          }

          for (const item of activeItems) {
            // Chỉ món perishable_drink (nước mía, nước sâm, chè pha sẵn) mới thiu bỏ;
            // Cà phê, sữa đặc, chanh tươi, trà mạn là 'storable' cất giữ dùng tiếp được 0đ hao hụt
            if (item.shelfLife === 'perishable_drink') {
              const wastedOrders = 2 + Math.floor(this.rng.next() * 3);
              const itemBaseWaste = item.costPrice * wastedOrders;
              const finalItemWaste = Math.round(itemBaseWaste * weatherWasteMultiplier * wasteReductionFactor);
              totalWaste += finalItemWaste;
            }
          }
        }

        const additionalCosts = totalWaste + depreciationCost + staffCost;
        const totalExpenses = rentPaid + additionalCosts;
        const nextMoney = Math.max(0, state.player.money - totalExpenses);
        const completedDayStats: DailyStats = {
          ...state.dayStats,
          rentPaid,
          wasteCost: totalWaste,
          depreciationCost,
          staffCost,
          cost: state.dayStats.cost + additionalCosts,
          profit: state.dayStats.profit - additionalCosts,
        };

        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            money: nextMoney,
          },
          dayStats: completedDayStats,
          dayHistory: [...state.dayHistory, completedDayStats],
          showDayReport: true,
          narrativeQuote: `Hết ngày ${state.dayNumber}! Doanh thu: ${completedDayStats.revenue.toLocaleString('vi-VN')}đ, Lợi nhuận ròng: ${completedDayStats.profit.toLocaleString('vi-VN')}đ (Khấu hao: -${depreciationCost.toLocaleString('vi-VN')}đ${staffCost > 0 ? `, Phụ quán: -${staffCost.toLocaleString('vi-VN')}đ` : ''}${totalWaste > 0 ? `, Hao hụt: -${totalWaste.toLocaleString('vi-VN')}đ` : ''}).`,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'DISMISS_DAY_REPORT': {
        const nextDay = state.dayNumber + 1;
        const DAYS_OF_WEEK = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];
        const nextDayOfWeek = DAYS_OF_WEEK[(nextDay - 1) % 7]!;
        const nextIsWeekend = nextDayOfWeek === 'Thứ Bảy' || nextDayOfWeek === 'Chủ Nhật';
        const newWeather = WeatherSystem.getWeatherForDay(nextDay, this.rng);

        // Kiểm tra xem ngày mới có đạt mốc sinh tồn nhận perk mới không
        const survivalPerk = getPerkForDay(nextDay);
        let updatedPerks = state.player.unlockedPerks ?? [];
        let pendingCelebration = state.pendingCelebration;

        if (survivalPerk && !updatedPerks.includes(survivalPerk.id)) {
          updatedPerks = [...updatedPerks, survivalPerk.id];
          pendingCelebration = {
            type: 'survival',
            levelOrDay: nextDay,
            title: `🏆 CỘT MỐC SINH TỒN: NGÀY ${nextDay}!`,
            subtitle: survivalPerk.name,
            items: [
              {
                id: survivalPerk.id,
                name: survivalPerk.name,
                icon: survivalPerk.icon,
                desc: survivalPerk.desc,
                type: 'perk',
              },
            ],
          };
        }
        // Cập nhật tình hình các quán đối thủ và dần xâm chiếm các vị trí ($) còn trống
        const playerStallId = state.player.ownedStalls[0];
        const playerNodeId = playerStallId ? state.stalls[playerStallId]?.nodeId : undefined;
        const nextDummyRivals = state.neighborhood
          ? RivalSystem.simulateRivalsTurn(
              state.dummyRivals ?? {},
              state.neighborhood.nodes,
              playerNodeId,
              nextDay,
              this.rng
            )
          : (state.dummyRivals ?? {});

        const nextState: GameState = {
          ...state,
          dayNumber: nextDay,
          currentShift: 'morning',
          dayOfWeek: nextDayOfWeek,
          isWeekend: nextIsWeekend,
          dummyRivals: nextDummyRivals,
          dayStats: {
            dayNumber: nextDay,
            startedAt: Date.now(),
            revenue: 0,
            cost: 0,
            profit: 0,
            salesCount: 0,
            rentPaid: 0,
          },
          weather: newWeather,
          showDayReport: false,
          player: {
            ...state.player,
            unlockedPerks: updatedPerks,
          },
          pendingCelebration,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'DISMISS_CELEBRATION': {
        const nextState: GameState = {
          ...state,
          pendingCelebration: null,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'ENABLE_UNLOCKED_ITEM': {
        const itemId = command.itemId;
        const currentActive = state.player.activeMenu ?? ['tra_da', 'tra_chanh'];
        const newActive = currentActive.includes(itemId) ? currentActive : [...currentActive, itemId];
        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            activeMenu: newActive,
          },
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'BUY_UPGRADE': {
        const upgDef = GAME_CONFIG.upgrades[command.upgradeId];
        if (!upgDef) return state;
        if (state.player.money < upgDef.cost) return state;
        if (state.player.upgrades[command.upgradeId]) return state;

        const newUpgrades = { ...state.player.upgrades, [command.upgradeId]: 1 };
        const reputationGain = upgDef.reputationBonus;

        const nextState: GameState = {
          ...state,
          player: {
            ...state.player,
            money: state.player.money - upgDef.cost,
            reputation: Math.min(100, state.player.reputation + reputationGain),
            upgrades: newUpgrades,
          },
          narrativeQuote: `Nâng cấp thành công: ${upgDef.name} (${upgDef.emoji})! ${upgDef.description}`,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'SET_MILESTONE': {
        return {
          ...state,
          pendingMilestone: command.milestone,
        };
      }

      case 'PAUSE_GAME': {
        const nextState: GameState = {
          ...state,
          phase: state.phase === 'PLAYING' ? 'PAUSED' : state.phase,
          isPaused: true,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'RESUME_GAME': {
        const nextState: GameState = {
          ...state,
          phase: state.phase === 'PAUSED' ? 'PLAYING' : state.phase,
          isPaused: false,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'GO_TO_LOBBY': {
        const nextState: GameState = {
          ...state,
          phase: 'LOBBY',
          isPaused: false,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      case 'RESTART_CURRENT_DAY': {
        const todayStats = state.dayStats;
        const restoredMoney = Math.max(0, state.player.money - todayStats.profit);
        const restoredTotalRevenue = Math.max(0, (state.player.totalRevenue ?? 0) - todayStats.revenue);
        const restoredTotalProfit = Math.max(0, (state.player.totalProfit ?? 0) - todayStats.profit);
        const restoredTotalSales = Math.max(0, (state.player.totalSales ?? 0) - todayStats.salesCount);

        const resetStalls = { ...state.stalls };
        for (const sid of state.player.ownedStalls) {
          if (resetStalls[sid]) {
            resetStalls[sid] = {
              ...resetStalls[sid],
              isSuspended: false,
              suspendedUntil: undefined,
            };
          }
        }

        const nextState: GameState = {
          ...state,
          phase: 'PLAYING',
          isPaused: false,
          currentShift: 'morning',
          activeEvent: null,
          showDayReport: false,
          player: {
            ...state.player,
            money: restoredMoney,
            totalRevenue: restoredTotalRevenue,
            totalProfit: restoredTotalProfit,
            totalSales: restoredTotalSales,
          },
          stalls: resetStalls,
          dayStats: {
            dayNumber: state.dayNumber,
            startedAt: Date.now(),
            revenue: 0,
            cost: 0,
            profit: 0,
            salesCount: 0,
            rentPaid: 0,
          },
          narrativeQuote: `Đã bắt đầu lại ngày ${state.dayNumber}. Chúc quán buôn may bán đắt!`,
        };
        SaveStore.save(nextState);
        return nextState;
      }

      default:
        return state;
    }
  }
}
